import { useEffect, useRef, useState, type FormEvent } from "react";
import s from "./Compliance.module.css";
import { useVerifyRule } from "../../api/queries";
import { coerceParameter, type Rule } from "../../domain/compliance";
import { Button, Field, Input } from "../../design/components";
import { t } from "../../i18n";

/**
 * Confirm a gated rule's thresholds against the aerodrome's certification
 * basis and enable it — for this tenant only. The basis is required, not
 * optional, because in an audit "who decided 0.43?" is the first question
 * and the platform stores the answer beside the confirmation.
 *
 * Every parameter is shown with the pack's default so a change is a
 * decision, not an accident.
 */
export function VerifyRuleDialog({
  rule,
  onClose,
  onVerified,
}: {
  rule: Rule;
  onClose: () => void;
  onVerified: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const verify = useVerifyRule();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(rule.parameters).map(([k, v]) => [k, typeof v === "string" ? v : JSON.stringify(v)]),
    ),
  );
  const [basis, setBasis] = useState(rule.confirmedAgainst ?? "");

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!basis.trim()) return;
    const parameters: Record<string, unknown> = {};
    for (const [k, text] of Object.entries(values)) {
      const v = coerceParameter(text, rule.packDefaults[k] ?? rule.parameters[k]);
      if (JSON.stringify(v) !== JSON.stringify(rule.packDefaults[k])) parameters[k] = v;
    }
    verify.mutate({ ruleKey: rule.key, parameters, confirmedAgainst: basis.trim() }, { onSuccess: onVerified });
  };

  return (
    <dialog ref={ref} onClose={onClose} style={dialogStyle} aria-label={t("Verify {rule}", { rule: rule.name })}>
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15 }}>
            {rule.enabled
              ? t("Re-confirm — {rule}", { rule: rule.name })
              : t("Verify and enable — {rule}", { rule: rule.name })}
          </h2>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>
            {rule.reference ?? t("No reference cited by the pack.")}{" "}
            {t(
              "This confirmation applies to this tenant only; the pack's defaults are unchanged and a pack upgrade cannot revert it.",
            )}
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {Object.keys(rule.parameters).map((k) => {
            const dflt = rule.packDefaults[k];
            const changed = values[k] !== (typeof dflt === "string" ? dflt : JSON.stringify(dflt));
            return (
              <Field
                key={k}
                label={changed ? t("{key} — pack default {value}", { key: k, value: JSON.stringify(dflt) }) : k}
              >
                <Input
                  value={values[k]}
                  onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                  className={changed ? s.paramChanged : undefined}
                  aria-label={k}
                />
              </Field>
            );
          })}
          {Object.keys(rule.parameters).length === 0 && (
            <div className="muted">{t("This rule has no parameters; verification records the basis only.")}</div>
          )}
        </div>

        <Field label="Confirmed against (required) — the certification basis, an AIP entry, a calibration certificate">
          <Input
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder={t("e.g. Aerodrome Manual Part D §3.4 (GACAR 139.327), issue 9")}
            required
            aria-label={t("Confirmed against")}
            autoFocus
          />
        </Field>

        {verify.isError && (
          <div className={s.gatedNote} role="alert">
            {t("Refused: {message}", { message: (verify.error as Error).message })}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={verify.isPending}>
            {t("Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={verify.isPending || !basis.trim()}>
            {verify.isPending ? t("Recording…") : rule.enabled ? t("Re-confirm thresholds") : t("Confirm and enable")}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(600px, 92vw)",
};
